import { useEffect } from 'react'
import { useLocation, useOutletContext } from 'react-router'

import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { CurrentUser } from '../../services/schema/User'
import { useGetUserPostsWithTokenQuery } from '../../services/apiSlice'
import { setPrevLocation, setPagination } from '../../services/appSlice'
import PostCard from './PostCard'

interface Props {
  username: string
}

const PostsByUser: React.FC<Props> = ({ username }) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const user = useOutletContext<CurrentUser>()
  const pagination = useAppSelector((state) => state.app.pagination.userPosts)
  const prevScrollLoc = useAppSelector(
    (state) => state.app.prevLocation.user.scroll,
  )

  const { data, isFetching, isSuccess, isError } =
    useGetUserPostsWithTokenQuery({
      page: pagination.page,
      username: username,
    })

  useEffect(() => {
    // also need to check if !isFetching because this means the app still has its cached data to populate the home component, therefore we will be able to land back to the previous scroll location no matter how far that is. If we do not have cached data, user should land at the top of home again
    if (!isFetching && prevScrollLoc !== null) {
      window.scrollTo(0, parseInt(prevScrollLoc, 10))
      dispatch(setPrevLocation({ loc: 'user', scroll: null, url: null }))
    }
    // else will not run when more posts is successfully fetched because this useEffect will only run during initial mount and location change
    else {
      window.scrollTo(0, 0)
      dispatch(setPrevLocation({ loc: 'user', scroll: null, url: null }))
    }
  }, [location])

  const userPosts =
    isSuccess && data ? (
      data.posts.map((post) => {
        return (
          <PostCard
            key={post.postId}
            id={post.postId}
            username={username}
            prev={'user'}
            canBeDeleted={user.username === username}
          />
        )
      })
    ) : (
      <div>Loading...</div>
    )

  const noPostsDisplay = (
    <h1 className="text-blue flex h-full w-full items-center justify-center font-bold">
      No posts to show
    </h1>
  )

  const errorDisplay = (
    <article className="border-theme hover-sub h-[80px] w-full">
      <h1 className="text-blue flex h-full w-full items-center justify-center font-bold">
        Something went wrong, unable to fetch more posts for now...
      </h1>
    </article>
  )

  const newerPostsRefetch = (
    <article className="border-theme hover-sub h-[60px] w-full border-r border-b border-l">
      <button
        type="button"
        onClick={() => {
          window.scrollTo(0, 0)
          dispatch(setPagination({ loc: 'user', reset: true }))
        }}
        className="text-blue h-full w-full font-bold"
      >
        Load latest posts?
      </button>
    </article>
  )

  return (
    <div className="w-full">
      <div className="user-container border-theme relative flex h-full w-full max-w-[600px] min-w-0 grow flex-col items-stretch pb-[60px] md:mr-0">
        {pagination.page > 1 && newerPostsRefetch}
        {data && userPosts}
        {!isError ? (
          <article className="hover-sub h-[80px] w-full">
            {data?.nextPage ? (
              <button
                type="button"
                onClick={() => {
                  window.scrollTo(0, 0)
                  dispatch(setPagination({ loc: 'user' }))
                }}
                className="text-blue h-full w-full font-bold"
              >
                Load older posts?
              </button>
            ) : (
              noPostsDisplay
            )}
          </article>
        ) : (
          errorDisplay
        )}
      </div>
    </div>
  )
}

export default PostsByUser
